import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Box, Link, Alert } from '@mui/material';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AuthService from '../services/auth.service';

interface ResetPasswordFormValues {
  new_password: string;
  confirm_password: string;
}

const ResetPasswordSchema = Yup.object().shape({
  new_password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('new_password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Extract token from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    } else {
      setToken(tokenParam);
    }
  }, [location]);

  const initialValues: ResetPasswordFormValues = {
    new_password: '',
    confirm_password: '',
  };

  const handleSubmit = async (
    values: ResetPasswordFormValues,
    { setSubmitting }: FormikHelpers<ResetPasswordFormValues>
  ) => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      setSubmitting(false);
      return;
    }

    try {
      setError(null);
      await AuthService.submitPasswordReset({
        token,
        new_password: values.new_password
      });
      setSuccess('Your password has been successfully reset. You can now log in with your new password.');
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.detail || 'Password reset failed. Please try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // If token is missing, show error
  if (error && !token) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/forgot-password"
            sx={{ mt: 2 }}
          >
            Request New Reset Link
          </Button>
        </Paper>
      </Container>
    );
  }

  // If successfully reset, show success message
  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Reset Password
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Formik
          initialValues={initialValues}
          validationSchema={ResetPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <Typography variant="body1" gutterBottom>
                Please enter your new password.
              </Typography>
              
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                id="new_password"
                name="new_password"
                label="New Password"
                type="password"
                error={touched.new_password && Boolean(errors.new_password)}
                helperText={touched.new_password && errors.new_password}
              />
              
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                id="confirm_password"
                name="confirm_password"
                label="Confirm New Password"
                type="password"
                error={touched.confirm_password && Boolean(errors.confirm_password)}
                helperText={touched.confirm_password && errors.confirm_password}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting}
                sx={{ mt: 3, mb: 2 }}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Back to Login
                </Link>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;
